import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card, Badge } from 'react-bootstrap';
import profile from '../../../assets/img/profile_av.png';

const MyProfile: React.FC = () => {
  const user = useSelector((state: any) => state.auth.user);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const initials = user?.name
    ? user.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : (user?.username?.[0] ?? 'U').toUpperCase();

  const groups: string[] = user?.userGroups ?? (user?.userGroup ? [user.userGroup] : []);

  return (
    <>
      {/* Page Header */}
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item">
                  <Link className="text-white" to="/"><i className="fa fa-home me-2"></i>Home</Link>
                </li>
                <li className="breadcrumb-item active text-white">My Profile</li>
              </ol>
            </div>
          </div>
          <div className="row align-items-center">
            <div className="col">
              <h1 className="fs-2 fw-bold mb-2 text-white">My Profile</h1>
              <p className="mb-0 text-white fs-6 opacity-75">Your account information and role details</p>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          <div className="row g-4">

            {/* Left — Avatar card */}
            <div className="col-lg-4">
              <Card className="border-0 shadow-sm text-center">
                <Card.Body className="py-5 px-4">
                  {user?.profileImage ? (
                    <img
                      src={user.profileImage}
                      alt={user.name || 'Profile'}
                      className="rounded-circle mb-3 shadow"
                      style={{ width: 110, height: 110, objectFit: 'cover', border: '4px solid #00CC00' }}
                    />
                  ) : (
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3 shadow"
                      style={{
                        width: 110, height: 110,
                        background: 'linear-gradient(135deg, #00FF00 0%, #00CC00 100%)',
                        fontSize: '2.2rem', fontWeight: 700, color: '#000',
                        border: '4px solid rgba(0,204,0,0.3)',
                      }}
                    >
                      {initials}
                    </div>
                  )}

                  <h4 className="fw-bold mb-1">{user?.name || user?.username || 'User'}</h4>
                  <p className="text-muted mb-3">{user?.email || '—'}</p>

                  <div className="d-flex flex-wrap gap-2 justify-content-center mb-3">
                    {groups.length > 0 ? groups.map((g: string) => (
                      <Badge
                        key={g}
                        style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.78rem', padding: '6px 12px', borderRadius: 8 }}
                      >
                        <i className="fa fa-shield me-1"></i>{g}
                      </Badge>
                    )) : (
                      <Badge bg="secondary">USER</Badge>
                    )}
                  </div>

                  <div className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                    style={{ backgroundColor: user?.enabled === false ? '#fce4e4' : '#e8f5e9' }}>
                    <span
                      className="rounded-circle d-inline-block"
                      style={{
                        width: 8, height: 8,
                        backgroundColor: user?.enabled === false ? '#c62828' : '#2e7d32',
                      }}
                    />
                    <span className="small fw-semibold" style={{ color: user?.enabled === false ? '#c62828' : '#2e7d32' }}>
                      {user?.status || (user?.enabled === false ? 'Inactive' : 'Active')}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </div>

            {/* Right — Details */}
            <div className="col-lg-8">
              <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white border-0 py-3 px-4">
                  <h5 className="mb-0 fw-bold d-flex align-items-center">
                    <i className="fa fa-user text-primary me-2"></i>Account Information
                  </h5>
                </Card.Header>
                <Card.Body className="px-4 py-3">
                  {[
                    { label: 'Full Name',       icon: 'fa-id-card',  value: user?.name || '—' },
                    { label: 'Username',         icon: 'fa-at',       value: user?.username || '—' },
                    { label: 'Email Address',    icon: 'fa-envelope', value: user?.email || '—' },
                    { label: 'Account Status',   icon: 'fa-toggle-on', value: user?.status || (user?.enabled === false ? 'Inactive' : 'Active') },
                    { label: 'Member Since',     icon: 'fa-calendar', value: formatDate(user?.createdAt) },
                    { label: 'Last Updated',     icon: 'fa-clock-o',  value: formatDate(user?.lastModifiedAt) },
                  ].map(row => (
                    <div
                      key={row.label}
                      className="d-flex align-items-center py-3"
                      style={{ borderBottom: '1px solid #f8f9fa' }}
                    >
                      <div
                        className="rounded-circle p-2 me-3 d-flex align-items-center justify-content-center"
                        style={{ backgroundColor: '#f0fdf4', border: '1px solid rgba(0,204,0,0.2)', width: 36, height: 36, flexShrink: 0 }}
                      >
                        <i className={`fa ${row.icon} text-success`} style={{ fontSize: '0.85rem' }}></i>
                      </div>
                      <div className="flex-grow-1">
                        <div className="small text-muted">{row.label}</div>
                        <div className="fw-semibold">{row.value}</div>
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>

              {/* Roles card */}
              {(user?.roles?.length > 0) && (
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white border-0 py-3 px-4">
                    <h5 className="mb-0 fw-bold d-flex align-items-center">
                      <i className="fa fa-key text-primary me-2"></i>Assigned Roles
                    </h5>
                  </Card.Header>
                  <Card.Body className="px-4 py-3">
                    <div className="d-flex flex-wrap gap-2">
                      {user.roles.map((role: any) => (
                        <span
                          key={role.id ?? role.name}
                          className="badge px-3 py-2"
                          style={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '0.82rem', borderRadius: 8 }}
                        >
                          <i className="fa fa-check-circle me-1"></i>
                          {role.name ?? role}
                        </span>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default MyProfile;
